// @flow

import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import SVG from 'react-inlinesvg';
import t from '~/src/services/i18n';
import { assetUrl } from '~/src/config/routes.js';
import { embedDataHandler } from '~/src/services/embedded';
import cx from 'classnames';
import { default as RcTooltip } from 'rc-tooltip';

import CodePanel from './components/CodePanel';
import FormPanel from './components/FormPanel';
import EditorHeader from './components/EditorHeader';
import PreviewPanel from './components/PreviewPanel';

import type { FieldSet } from '~/src/config/form';

import './EditorPage.scss';

type Props = {|
  formConfig: Array<FieldSet>,
  interactiveDevice: 'desktop' | 'tablet' | 'phone',
  setFormValue: Function,
  getFormStateForForm: Object,
  formCode: string,
  widgetPropsFromFormState: Object,
  resetForm: Function,
  initFormState: Function,
  setDefaults: Function,
  codeConfigOnly: boolean,
  setCodeConfigOnly: Function,
  isMobile?: boolean
|}

type State = {
  display: 'customize' | 'preview' | 'code',
  embedded?: boolean
}

const injectedProps = (stores: Object) => {
  const {
    setFormValue,
    widgetPropsFromFormState,
    getFormStateForForm,
    codeFromConfig,
    formConfig,
    interactiveCode,
    formCode,
    resetForm,
    initFormState,
    setDefaults,
    codeConfigOnly,
    setCodeConfigOnly,
    isMobile
  } = stores.editorStore;
  return {
    setFormValue,
    widgetPropsFromFormState,
    getFormStateForForm,
    codeFromConfig,
    formConfig,
    interactiveCode,
    formCode,
    resetForm,
    codeConfigOnly,
    setCodeConfigOnly,
    initFormState,
    setDefaults,
    isMobile
  };
};

@observer
class EditorPage extends Component<Props, State> {

  state = {
    display: window.innerWidth > 1023 ? 'customize' : 'preview',
    embedded: false,
    codeTabTooltipShown: localStorage.getItem('code-tooltip') != 'hide'
  };

  componentDidMount() {
    // Handle Embedded Setup
    const isEmbedded = window.self !== window.top;
    if (isEmbedded) {
      this.handleEmbeddedData();
      this.setState({ embedded: true });
    }
  }

  handleEmbeddedData = () => {
    const customAction = window.cloudinary.customAction;
    if (customAction) {
      customAction.getConfig().then(data => {
        const mediaAssets = embedDataHandler(data);
        if (mediaAssets) {
          this.props.setFormValue('mediaAssets', mediaAssets);
        }
      });
    }
  }

  handleTooltipVisibility = () => {
    if (localStorage.getItem('code-tooltip') != 'hide') {
      this.setState({ codeTabTooltipShown: false });
      localStorage.setItem('code-tooltip', 'hide');
    }
  }

  render() {
    const {
      formConfig,
      formCode,
      setFormValue,
      getFormStateForForm,
      widgetPropsFromFormState,
      resetForm,
      initFormState,
      setDefaults,
      codeConfigOnly,
      setCodeConfigOnly,
    } = this.props;

    const { display } = this.state;

    return (
      <div className={cx(
        'editor-container',
        { 'embedded': this.state.embedded }
      )} >
        {!this.state.embedded &&
          <div className="editor-header" >
            <EditorHeader />
          </div >
        }
        <ul className="editor-tabs" >
          <li
            className={cx('tab', 'customize', { selected: display === 'customize' })}
            onClick={() => this.setState({ display: 'customize' })}
          >
            <SVG src={assetUrl('Icons/customize.svg')} className="icon" />
            {t('customize')}
          </li >
          <li
            className={cx('tab', 'preview', { selected: display === 'preview' })}
            onClick={() => this.setState({ display: 'preview' })}
          >
            <SVG src={assetUrl('Icons/layout-classic.svg')} className="icon" />
            {t('preview')}
          </li >
          <RcTooltip
            placement="right"
            overlay={
              <div>
                {t('codeTabTooltip')}
                <button
                  className="btn btn-icon"
                  onClick={() => this.handleTooltipVisibility()}
                >
                  <SVG src={assetUrl('Icons/close.svg')} />
                </button>
              </div>
            }
            visible={this.state.codeTabTooltipShown && !this.props.isMobile}
            overlayClassName="code-tab-tooltip"
          >
            <li
              className={cx('tab', 'code', { selected: display === 'code' })}
              onClick={() => { this.setState({ display: 'code' }); this.handleTooltipVisibility();}}
            >
              <SVG src={assetUrl('Icons/code.svg')} className="icon" />
              {t('code')}
            </li >
          </RcTooltip>
        </ul >
        <div className="editor-stage" >
          <div
            className={cx(
              'editor-panel form',
              { 'hide': display !== 'customize' },
              { 'is-mobile': this.props.isMobile == true },
            )}
            onScroll={() => this.handleTooltipVisibility()}
          >
            <FormPanel
              formConfig={formConfig}
              setFormValue={setFormValue}
              formState={getFormStateForForm}
              isMobile={this.props.isMobile}
            />
          </div >
          <div className={cx('editor-panel preview', { hide: display !== 'preview' })} >
            {widgetPropsFromFormState && widgetPropsFromFormState.mediaAssets &&
              <PreviewPanel
                initFormState={initFormState}
                widgetPropsFromFormState={widgetPropsFromFormState}
                setDefaults={setDefaults}
                embedded={this.state.embedded || false}
              />
            }
          </div >
          <div className={cx('editor-panel code', { hide: display !== 'code' })} >
            <CodePanel
              code={formCode}
              resetForm={resetForm}
              codeConfigOnly={codeConfigOnly}
              setCodeConfigOnly={setCodeConfigOnly}
            />
          </div >
        </div >
      </div >
    );
  }
}

export default inject(injectedProps)(EditorPage);
